<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import VarsityInlineConfirmGroup from "@/components/VarsityInlineConfirmGroup.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { ChampionshipRegistrationAdditionalCost } from "@/model/championship/championship-registration-additional-cost"
import type { ChampionshipRegistrationTeam } from "@/model/championship/championship-registration-team"
import type { ChampionshipRegistrationTeamCancellationTask } from "@/model/championship/championship-registration-team-cancellation-task"
import type { RegistrationPermissions } from "@/pages/Tasks/Tasks/championship-registration"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { defineTranslations } from "@/utils/defineTranslations"
import { $currency, useHttpClient, useHttpGet } from "@/vf"
import { computed, ref, toRefs, watch } from "vue"
import ChampionshipRegistrationAdditionalCostsSectionAdmin from "./internal/ChampionshipRegistrationAdditionalCostsSectionAdmin.vue"
import { useRegistrationPolicy } from "./useRegistrationPolicy"

type TeamCancellationData = {
    registrationId: string
    organizationName?: string
    teamId?: string
    teamName?: string
    team?: ChampionshipRegistrationTeam
    teamTotalPrice: number
    lateTeamCancelFeePercent?: number | null
    teamAdditionalCosts?: ChampionshipRegistrationAdditionalCost[]
    additionalCost?: {
        id: string
        name: string
        price: number
        quantity: number
    } | null
    permissions: RegistrationPermissions
}

const props = defineProps<TasksProps<ChampionshipRegistrationTeamCancellationTask>>()
const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const http = useHttpClient()
const { data, refresh } = await useHttpGet<TeamCancellationData>(
    `/championship/registration/${task.value.organizationId}/team-cancellation/${task.value.id}`,
)

const feeInEuro = ref<number>(0)
const feeInitialized = ref(false)
const feeInCents = computed(() => Math.round((feeInEuro.value || 0) * 100))
const feeValid = computed(() => Number.isFinite(feeInEuro.value) && feeInEuro.value >= 0)

const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => props.viewMode),
)

const withoutFeeConfirm = createInlineConfirm({
    async action() {
        await http.post(
            `/championship/registration/team-cancellation/${task.value.id}/approve-without-fee?viewMode=${props.viewMode}`,
        )
    },
    async afterAction() {
        await refresh()
        emit("updated")
    },
})

const submitWithFee = createInlineConfirm({
    async action() {
        await http.post(
            `/championship/registration/team-cancellation/${task.value.id}/approve-with-fee?viewMode=${props.viewMode}`,
            {
                fee: feeInCents.value,
            },
        )
    },
    async afterAction() {
        await refresh()
        emit("updated")
    },
})

const suggestedFeePercent = computed(() => data.value?.lateTeamCancelFeePercent ?? null)
const suggestedFeeCents = computed(() =>
    suggestedFeePercent.value !== null
        ? Math.round(((data.value?.teamTotalPrice ?? 0) * suggestedFeePercent.value) / 100)
        : 0,
)

watch(
    () => data.value?.teamTotalPrice,
    totalPrice => {
        if (feeInitialized.value || totalPrice === undefined) {
            return
        }

        feeInEuro.value = suggestedFeeCents.value / 100
        feeInitialized.value = true
    },
    { immediate: true },
)

const trans = defineTranslations({
    customer_info: {
        de: 'Du hast dein Team "{teamName}" storniert. Ein Mitarbeiter kümmert sich darum.',
        en: 'You canceled your team "{teamName}". A staff member will take care of it.',
    },
    admin_info: {
        de: 'Der Kunde möchte dieses Team stornieren. Bitte entscheide ob Stornogebühren anfallen sollen. Wenn das Team doch nicht storniert werden soll akzeptiere die Stornierung ohne Gebühren und setze den Status im "Prüfung Deiner Anmeldung"-Task auf freigegeben.',
        en: 'The customer wants to cancel this team. Please decide if cancellation fees should apply. If the team should not be canceled, approve the cancellation without fees and set the status in the "Review your registration" task to approved.',
    },
    admin_title: {
        de: "Wie soll die Stornierung bearbeitet werden?",
        en: "How should the cancellation be processed?",
    },
    without_fee: {
        de: "Stornieren ohne Gebühren",
        en: "Cancel without fees",
    },
    with_fee: {
        de: "Stornieren mit Gebühren",
        en: "Cancel with fees",
    },
    fee_label: {
        de: "Gebühr",
        en: "Fee",
    },
    organization_label: {
        de: "Organisation",
        en: "Organization",
    },
    team_label: {
        de: "Team",
        en: "Team",
    },
    suggested_fee_percent: {
        de: "Vorschlag ({percent}%): {amount}",
        en: "Suggested ({percent}%): {amount}",
    },
    without_fee_confirmation: {
        de: "Möchtest du das Team wirklich ohne Gebühren stornieren?",
        en: "Do you really want to cancel the team without fees?",
    },
    without_fee_success: {
        de: "Die Stornierung wurde ohne Gebühren abgeschlossen.",
        en: "The cancellation was completed without fees.",
    },
    with_fee_confirmation: {
        de: "Möchtest du das Team wirklich mit Gebühren stornieren?",
        en: "Do you really want to cancel the team with fees?",
    },
    with_fee_success: {
        de: "Die Stornierung wurde mit Gebühren abgeschlossen.",
        en: "The cancellation was completed with fees.",
    },
    decision_with_fee: {
        de: "Die Stornierung wurde mit Gebühren abgeschlossen: {amount}",
        en: "The cancellation was completed with fees: {amount}",
    },
    decision_without_fee: {
        de: "Die Stornierung wurde ohne Gebühren abgeschlossen.",
        en: "The cancellation was completed without fees.",
    },
})
</script>

<template>
    <div class="p-4">
        <div class="alert alert-info" v-if="props.viewMode === 'customer' && props.task.state === 'open'">
            {{ trans("customer_info", { teamName: data?.teamName ?? "" }) }}
        </div>

        <template v-if="props.task.state === 'closed'">
            <div class="alert alert-success mb-4 py-4 px-4">
                <i class="fa fa-check-circle text-success mr-2"></i>
                <template v-if="data?.additionalCost">
                    {{
                        trans("decision_with_fee", {
                            amount: $currency((data.additionalCost.price ?? 0) * (data.additionalCost.quantity ?? 1)),
                        })
                    }}
                </template>
                <template v-else>
                    {{ trans("decision_without_fee") }}
                </template>
            </div>
        </template>

        <template v-if="props.viewMode === 'admin'">
            <div class="alert alert-warning mb-4" v-if="props.task.state === 'open'">
                {{ trans("admin_info") }}
            </div>

            <div class="card mb-4">
                <div class="card-body py-3">
                    <div class="row">
                        <div class="col-md-6 mb-2 mb-md-0">
                            <div class="small text-muted">{{ trans("organization_label") }}</div>
                            <div class="font-weight-bold">{{ data?.organizationName || "-" }}</div>
                        </div>
                        <div class="col-md-6">
                            <div class="small text-muted">{{ trans("team_label") }}</div>
                            <div class="font-weight-bold">{{ data?.teamName || "-" }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="card mt-4" v-if="props.task.state === 'open' && policy.CancelTeamApprove">
                <div class="card-body">
                    <div class="mb-3 font-weight-bold">{{ trans("admin_title") }}</div>

                    <div class="mb-3" style="max-width: 260px">
                        <b>{{ trans("fee_label") }}</b>
                        <input type="number" min="0" step="0.01" class="form-control" v-model.number="feeInEuro" />
                        <small class="text-muted d-block mt-1" v-if="suggestedFeePercent !== null">
                            {{
                                trans("suggested_fee_percent", {
                                    percent: suggestedFeePercent,
                                    amount: $currency(suggestedFeeCents),
                                })
                            }}
                        </small>
                    </div>

                    <VarsityInlineConfirmGroup>
                        <VarsityInlineConfirmButton
                            :controller="withoutFeeConfirm"
                            class="btn btn-primary btn-shape-skewed"
                        >
                            {{ trans("without_fee") }}
                        </VarsityInlineConfirmButton>
                        <VarsityInlineConfirmButton
                            :controller="submitWithFee"
                            class="btn btn-danger btn-shape-skewed"
                            :disabled="!feeValid"
                        >
                            {{ trans("with_fee") }}
                        </VarsityInlineConfirmButton>
                    </VarsityInlineConfirmGroup>

                    <VarsityInlineConfirm :controller="withoutFeeConfirm">
                        <template #confirmation>{{ trans("without_fee_confirmation") }}</template>
                        <template #success>{{ trans("without_fee_success") }}</template>
                    </VarsityInlineConfirm>

                    <VarsityInlineConfirm :controller="submitWithFee">
                        <template #confirmation>{{ trans("with_fee_confirmation") }}</template>
                        <template #success>{{ trans("with_fee_success") }}</template>
                    </VarsityInlineConfirm>
                </div>
            </div>

            <ChampionshipRegistrationAdditionalCostsSectionAdmin
                v-if="data?.team"
                class="mt-4"
                :registration-id="data.registrationId"
                :team="data.team"
                :additional-costs="data.teamAdditionalCosts ?? []"
                :permissions="data?.permissions"
                @updated="refresh(), emit('updated')"
            />
        </template>
    </div>
</template>
